import _ from 'lodash';
import { createSelector } from 'reselect';

import { getItemsListOrdered } from '../../../redux/list/list.utils';
import { StoreState } from '../../../redux/reducers/store.types';
import { getCurrentOrganizationOwnerId } from '../../../selectors/organizations/organizations';
import {
  isMemberOrganizationAdmin,
  isMemberOrganizationOwner,
  isMemberOrganizationViewer,
  isMemberProjectAdmin,
  isMemberProjectViewer,
} from '../team-member-modal/team-member-form/team-member-form.utils';
import { TEAM_MEMBERS_FILTER_BY_PROJECT_OPTION_ALL } from '../team-projects-filter/team-projects-filter';
import { TeamMemberRoles } from '../team.constants';
import { TeamState } from '../team.reducer';
import {
  getTeamMembersSearchValue,
  getTeamMembersSelectedProjectId,
  getTeamMembersSelectedRole,
} from '../team.selectors-ui';
import { TeamMember } from '../team.types';

const getTeamState = (state: StoreState): TeamState => state.team;

export const getTeamMembers = createSelector(
  getTeamState,
  (state: TeamState): TeamMember[] =>
    getItemsListOrdered(state.membersListState),
);

export const getProjectTeamMembers = createSelector(
  getTeamState,
  (state: TeamState): TeamMember[] =>
    getItemsListOrdered(state.projectMembersListState),
);

export const getTeamInvitations = createSelector(
  getTeamState,
  (state: TeamState): TeamMember[] =>
    getItemsListOrdered(state.invitationsListState),
);

export const getProjectTeamInvitations = createSelector(
  getTeamState,
  (state: TeamState): TeamMember[] =>
    getItemsListOrdered(state.projectInvitationsListState),
);

export const getTeamMembersFiltered = createSelector(
  getCurrentOrganizationOwnerId,
  getTeamMembers,
  getTeamInvitations,
  getTeamMembersSelectedProjectId,
  getTeamMembersSelectedRole,
  getTeamMembersSearchValue,
  (
    organizationOwnerId: string,
    members: TeamMember[],
    invitations: TeamMember[],
    selectedProjectId: string,
    selectedRole: TeamMemberRoles,
    searchValue: string,
  ): TeamMember[] => {
    const allMembers = [...members, ...invitations];

    const filteredMembersBySearch = _.filter(
      allMembers,
      (member: TeamMember): boolean => {
        if (!searchValue) {
          return true;
        }

        return (
          _.includes(_.toLower(member.name), _.toLower(searchValue)) ||
          _.includes(
            _.toLower(member.primaryEmail.address),
            _.toLower(searchValue),
          )
        );
      },
    );

    const projectId =
      selectedProjectId === TEAM_MEMBERS_FILTER_BY_PROJECT_OPTION_ALL
        ? undefined
        : selectedProjectId;

    return _.filter(filteredMembersBySearch, (member: TeamMember) => {
      switch (selectedRole) {
        case TeamMemberRoles.Owner:
          return (
            member.id === organizationOwnerId ||
            isMemberOrganizationOwner(member.securityGroups)
          );

        case TeamMemberRoles.Admin:
          return isMemberOrganizationAdmin(member.securityGroups);

        case TeamMemberRoles.Viewer:
          return (
            isMemberOrganizationViewer(member.securityGroups) ||
            isMemberProjectViewer(member.securityGroups, projectId)
          );

        case TeamMemberRoles.Editor:
          return isMemberProjectAdmin(member.securityGroups, projectId);

        default:
          return true;
      }
    });
  },
);
